import type { RichtextSectionProps } from '@/components/sections'
import type { Contentblock } from '@/kontent/content-types/contentblock'

import { EMPTY_KONTENT_RICHTEXT } from '@/constants'
import type { WebUiCardCarousel } from '@/kontent/content-types/web_ui_card_carousel'
import { getLinkFromLinkUI } from '@/utils/getLinkFromLinkUI'
import { Card, CardCopy } from 'nsw-ds-react'
import { useEffect, useRef } from 'react'
import { CardHeadline } from '../cards/CardHeadline'
import { ModuleSectionWrapper } from '../ModuleSectionWrapper'
import SanitisedHTMLContainer from '../SanitisedHTMLContainer'
import {
	getLinkedItems,
	useKontentHomeConfig,
} from '../contexts/KontentHomeConfigProvider'
import {
	getCardStyleBasedOnUiCardsThemeOption,
	type UiCardsThemeOptions,
} from './ui_cards'

const darkSectionParentSelector = [
	'.nsw-section--brand-dark &',
	'.nsw-section--brand-supplementary &',
	'.nsw-section--black &',
	'.nsw-section--grey-01 &',
	'.nsw-section--grey-02 &',
	'.nsw-section--invert &',
].join(', ')

export default function UiCardCarouselComponent(
	props: RichtextSectionProps<WebUiCardCarousel>,
) {
	const { linkedItem, mappings } = props
	const { pageResponseLinkedItems } = useKontentHomeConfig()
	const carouselRef = useRef<HTMLDivElement>(null)

	const title = linkedItem.elements?.title?.value
	const theme = (linkedItem.elements?.theme?.value?.[0]?.codename ||
		'white') as UiCardsThemeOptions
	const pagination = linkedItem.elements?.pagination?.value?.[0]?.codename
	const showNumberedPagination = pagination === 'yes'
	const displayMode =
		linkedItem.elements?.display?.value?.[0]?.codename ||
		'headline_and_copy'
	const showImage =
		displayMode === 'image' || displayMode === 'image_horizontal'
	const showCopy = displayMode !== 'headline_only'

	const items = getLinkedItems(
		linkedItem.elements?.items,
		pageResponseLinkedItems,
	)

	useEffect(() => {
		if (!carouselRef.current) return
		// @ts-ignore - NSW DS carousel JS copied locally for bundling
		import('@/lib/nsw-carousel/carousel').then((mod) => {
			const Carousel = mod.default
			const carousel = new Carousel(carouselRef.current)
			carousel.init()
		})
	}, [])

	const renderTile = (item: Contentblock) => {
		const navItem = getLinkedItems(
			item.elements.more_info_link,
			pageResponseLinkedItems,
		)[0]

		const { url, target, linkComponent, isExternal } = getLinkFromLinkUI(
			navItem,
			mappings,
			pageResponseLinkedItems,
		)

		const imageAsset = item.elements.image.value?.[0]
		const imageUrl = showImage && imageAsset ? imageAsset.url : undefined

		return (
			<Card
				headline={
					<CardHeadline
						title={item.elements.title.value}
						isExternal={isExternal}
					/>
				}
				{...(imageUrl
					? {
							image: imageUrl,
							imageAlt: imageAsset?.description || '',
					  }
					: {})}
				link={url}
				linkComponent={linkComponent}
				linkTarget={target}
				className={
					displayMode === 'image_horizontal'
						? 'nsw-card--horizontal'
						: undefined
				}
				data-kontent-item-id={item.system.id}
				style={getCardStyleBasedOnUiCardsThemeOption(theme)}
				highlight={theme === 'highlight'}
				css={
					theme === 'brand_dark'
						? {
								[darkSectionParentSelector]: {
									border: '1px solid var(--nsw-white)',
								},
						  }
						: undefined
				}
			>
				{showCopy &&
					item.elements.content.value !== EMPTY_KONTENT_RICHTEXT && (
						<CardCopy>
							<div
								className="overflow-hidden"
								css={{
									display: '-webkit-box',
									WebkitLineClamp: 4,
									WebkitBoxOrient: 'vertical',
								}}
							>
								<SanitisedHTMLContainer>
									{item.elements.content.value}
								</SanitisedHTMLContainer>
							</div>
						</CardCopy>
					)}
			</Card>
		)
	}

	return (
		<ModuleSectionWrapper>
			{title && <h2 className="nsw-section-title md:mb-4">{title}</h2>}
			<div className="nsw-carousel-container">
				<div
					ref={carouselRef}
					className="nsw-carousel js-carousel"
					data-drag="on"
					data-loop="on"
					data-navigation="on"
					data-navigation-pagination={
						showNumberedPagination ? 'on' : 'off'
					}
					data-justify-content="on"
					css={{
						'.nsw-carousel__nav-item:not(.nsw-carousel__nav-item--selected) button:hover':
							{
								backgroundColor: 'var(--nsw-hover)',
							},
						[darkSectionParentSelector]: {
							'.nsw-carousel__control': {
								borderColor: 'var(--nsw-text-light)',
								'&:hover': {
									backgroundColor: 'var(--nsw-off-white)',
								},
							},
							'.nsw-carousel__nav-item button': {
								borderColor: 'var(--nsw-off-white)',
								color: 'var(--nsw-off-white)',
							},
							'.nsw-carousel__nav-item--selected button': {
								backgroundColor: 'var(--nsw-off-white)',
								borderColor: 'var(--nsw-off-white)',
								color: 'var(--nsw-text-dark)',
								'&:hover': {
									backgroundColor: 'var(--nsw-off-white)',
								},
							},
							'.nsw-carousel__nav-item:not(.nsw-carousel__nav-item--selected) button:hover':
								{
									backgroundColor: 'var(--nsw-off-white)',
									color: 'var(--nsw-text-dark)',
								},
						},
						...(showNumberedPagination
							? {}
							: {
									'.nsw-carousel__nav-item--selected button':
										{
											borderRadius: '50%',
											width: '1.2em',
											'@media (min-width: 48em)': {
												width: '0.9em',
											},
										},
							  }),
					}}
				>
					<nav aria-label="Carousel controls">
						<ul>
							<li>
								<button
									aria-label="Show previous items"
									className="nsw-carousel__control nsw-carousel__control--prev js-carousel__control"
								>
									<svg
										className="nsw-icon"
										viewBox="0 0 20 20"
									>
										<title>Show previous items</title>
										<polyline
											points="13 2 5 10 13 18"
											fill="none"
											stroke="currentColor"
											strokeWidth="2"
										/>
									</svg>
								</button>
							</li>
							<li>
								<button
									aria-label="Show next items"
									className="nsw-carousel__control nsw-carousel__control--next js-carousel__control"
								>
									<svg
										className="nsw-icon"
										viewBox="0 0 20 20"
									>
										<title>Show next items</title>
										<polyline
											points="7 2 15 10 7 18"
											fill="none"
											stroke="currentColor"
											strokeWidth="2"
										/>
									</svg>
								</button>
							</li>
						</ul>
					</nav>
					<div className="nsw-carousel__wrapper js-carousel__wrapper">
						<ol className="nsw-carousel__list">
							{items.map((item: Contentblock) => (
								<li
									key={item.system.id}
									className="nsw-carousel__item"
								>
									{renderTile(item)}
								</li>
							))}
						</ol>
					</div>
				</div>
			</div>
		</ModuleSectionWrapper>
	)
}
